import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";

const empty = { name: "", is_team: false, fee: 0, fee_per_member: false, age_groups: [], max_signups: "" };
const ctl = "w-full h-11 px-3 rounded-xl bg-[#0B132B] border border-blue-500/20 focus:border-blue-500 outline-none text-sm mt-1";

export const CategoryDialog = ({ open, cat, onClose, onSaved }) => {
  const [f, setF] = useState(empty);
  const [age, setAge] = useState("");
  const [ageOpts, setAgeOpts] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF(cat ? { ...empty, ...cat } : empty); setAge(""); api.get("/admin/options").then((r) => setAgeOpts(r.data.age_group || [])); } }, [open, cat]);
  const counts = cat?.signup_ages || {};
  const renamed = !!cat && f.name.trim().toUpperCase() !== cat.name && (!!cat.performances || !!cat.signups);

  const addAge = () => {
    const a = age.trim();
    if (a && !f.age_groups.includes(a)) setF({ ...f, age_groups: [...f.age_groups, a] });
    setAge("");
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { name: f.name, is_team: f.is_team, fee: Number(f.fee), fee_per_member: f.fee_per_member, age_groups: f.age_groups, max_signups: f.max_signups ? Number(f.max_signups) : null };
      if (cat) await api.put(`/admin/categories/${cat.id}`, body); else await api.post("/admin/categories", body);
      toast.success(cat ? "Zapisano zmiany kategorii" : "Dodano kategorię");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#1C2541] border-blue-500/30 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="category-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{cat ? "Edytuj kategorię" : "Nowa kategoria"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <label className="block text-xs text-slate-400">Nazwa kategorii
            <input data-testid="category-name-input" required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={`${ctl} disabled:opacity-50`} placeholder="np. AERIAL SILKS SOLO DEBUT" />
            {renamed && <span className="text-[11px] text-amber-300">Nowa nazwa zostanie zmieniona także w zgłoszeniach i na liście startowej w Organizatorze.</span>}
          </label>
          <label className="flex items-center gap-3 text-sm"><Switch data-testid="category-team-switch" checked={f.is_team} onCheckedChange={(v) => setF({ ...f, is_team: v })} />Duet / grupa / formacja (kilka osób)</label>
          <div className="grid grid-cols-2 gap-3 items-end">
            <label className="block text-xs text-slate-400">Cena za zapis (zł)
              <input data-testid="category-fee-input" type="number" min="0" step="1" required value={f.fee} onChange={(e) => setF({ ...f, fee: e.target.value })} className={ctl} />
            </label>
            <label className="flex items-center gap-2 text-sm h-11"><Switch data-testid="category-per-member-switch" checked={f.fee_per_member} onCheckedChange={(v) => setF({ ...f, fee_per_member: v })} />za każdą osobę</label>
          </div>
          <label className="block text-xs text-slate-400">Limit zgłoszeń w kategorii (puste = bez limitu)
            <input data-testid="category-limit-input" type="number" min="1" step="1" value={f.max_signups ?? ""} onChange={(e) => setF({ ...f, max_signups: e.target.value })} className={ctl} placeholder="bez limitu" />
            <span className="text-[11px] text-slate-500">Liczy oczekujące i zatwierdzone zgłoszenia (duet lub grupa = 1 zgłoszenie). Po osiągnięciu limitu kategoria jest niedostępna w zapisach.</span>
          </label>
          <div className="text-xs text-slate-400">Grupy wiekowe
            <div className="flex flex-wrap gap-2 mt-2" data-testid="category-age-list">
              {f.age_groups.map((a) => (
                <span key={a} className="inline-flex items-center gap-1 h-8 pl-3 pr-1 rounded-full bg-blue-600/15 border border-blue-500/30 text-blue-100 text-xs">
                  {a}{counts[a] ? <span className="text-slate-400">({counts[a]})</span> : null}
                  <button type="button" data-testid={`category-age-remove-${a}`} disabled={!!counts[a]} title={counts[a] ? "W tej grupie są zawodnicy" : "Usuń"}
                    onClick={() => setF({ ...f, age_groups: f.age_groups.filter((x) => x !== a) })} className="h-6 w-6 rounded-full hover:bg-white/10 flex items-center justify-center disabled:opacity-30"><X className="w-3 h-3" /></button>
                </span>
              ))}
              {!f.age_groups.length && <span className="text-amber-300">Dodaj co najmniej jedną grupę, aby kategoria była dostępna w zapisach.</span>}
            </div>
            <p className="text-[11px] text-slate-500 mt-2">Wiek zawodnika w zapisach musi pasować do grupy (rok zawodów minus rok urodzenia). Nowe grupy wiekowe dodasz, edytując zawodnika w Organizatorze (pole Wiek).</p>
            <div className="flex gap-2 mt-2">
              <select data-testid="category-age-input" value={age} onChange={(e) => setAge(e.target.value)} className={`${ctl} mt-0`}>
                <option value="">— wybierz grupę wiekową —</option>
                {ageOpts.filter((a) => !f.age_groups.includes(a)).map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <button type="button" data-testid="category-age-add" onClick={addAge} className="h-11 px-3 rounded-xl border border-blue-500/30 text-blue-200 hover:bg-blue-500/10 flex items-center gap-1 text-sm shrink-0"><Plus className="w-4 h-4" />Dodaj</button>
            </div>
          </div>
          <button data-testid="category-save-btn" disabled={busy} className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-500 font-semibold disabled:opacity-40 flex items-center justify-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}Zapisz
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
